'use client';

import { useQuery } from '@tanstack/react-query';
import { DocumentNode, print } from 'graphql';

import { GET_EVM_NFTS_BY_OWNERS } from '@repo/api-core-toolkit';
import {
  EVMNft,
  EvmNFTChain,
  EvmNFTCollectionContractName,
} from '@repo/types-client';

import { getEvmNfts } from '../utils';

export interface UseEvmNftsProps {
  chain: EvmNFTChain;
  dataSource: 'indexer' | 'onchain';
  tokenId?: undefined;
  address?: undefined;
  addresses?: undefined;
  enabled?: boolean;
  additionalQueryKeys?: string[] | number[];
}

export interface UseEvmNftsSingularProps
  extends Omit<UseEvmNftsProps, 'dataSource' | 'tokenId'> {
  dataSource: 'onchain'; // TODO: Remove this when the indexer implementation is ready.
  tokenId: string;
  collections: [EvmNFTCollectionContractName];
}

export interface UseEvmNftsSingularERC1155Props
  extends Omit<UseEvmNftsProps, 'dataSource' | 'tokenId' | 'address'> {
  dataSource: 'onchain'; // TODO: Remove this when the indexer implementation is ready.
  tokenId: string;
  address: string | undefined;
  collections: [EvmNFTCollectionContractName];
}

export interface UseEvmNftsPluralProps
  extends Omit<UseEvmNftsProps, 'addresses'> {
  addresses: string[];
  collections: EvmNFTCollectionContractName[];
}

export interface UseEvmNftsResult {
  loading: boolean;
  refetch: () => void;
}

export interface UseEvmNftsSingularResult<T extends EVMNft>
  extends UseEvmNftsResult {
  data: T | undefined;
}

export interface UseEvmNftsPluralResult<T extends EVMNft>
  extends UseEvmNftsResult {
  data: T[] | undefined;
}

export function useEvmNfts<T extends EVMNft>(
  props: UseEvmNftsSingularProps,
): UseEvmNftsSingularResult<T>;
export function useEvmNfts<T extends EVMNft>(
  props: UseEvmNftsSingularERC1155Props,
): UseEvmNftsSingularResult<T>;
export function useEvmNfts<T extends EVMNft>(
  props: UseEvmNftsPluralProps,
): UseEvmNftsPluralResult<T>;

/**
 * Hook to fetch EVM NFTs from the indexer or on-chain based on the data source.
 *
 * - If you need the most up-to-date data, use the 'onchain' data source.
 * You will normally use this data source on screens that modify the state of the NFTs.
 *
 * - If you need the data fast, use the 'indexer' data source.
 * You will normally use this data source on screens that only display the NFTs.
 *
 * @param collections - The collections to fetch NFTs for.
 * @param dataSource - The data source.
 * @param chain - The chain to fetch NFTs from.
 * @param address - The address to fetch an NFT for. Optional. Only available when fetching a singular ERC1155.
 * @param addresses - The addresses to fetch NFTs for. Optional
 * @param tokenId - The token ID to fetch. Optional.
 * @param enabled - Whether the query is enabled.
 *
 * @returns The EVM NFTs.
 */
export function useEvmNfts<T extends EVMNft>(
  props:
    | UseEvmNftsSingularProps
    | UseEvmNftsSingularERC1155Props
    | UseEvmNftsPluralProps,
): UseEvmNftsSingularResult<T> | UseEvmNftsPluralResult<T> {
  const {
    address,
    addresses,
    collections,
    chain,
    dataSource,
    enabled,
    tokenId,
    additionalQueryKeys = [],
  } = props;

  const isOnchain = dataSource === 'onchain';
  const isIndexer = dataSource === 'indexer';

  const API_URL =
    process.env.NEXT_PUBLIC_GQL_API_URL ||
    'https://core-api.dev.doodles.app/graphql';

  const {
    data: dataIndexer,
    isLoading: loadingIndexer,
    isError: errorIndexer,
    refetch: refetchIndexer,
  } = useQuery<T[]>({
    queryKey: [
      'evmNftsIndexer',
      addresses,
      collections,
      ...additionalQueryKeys,
    ],
    queryFn: async () => {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          query: print(GET_EVM_NFTS_BY_OWNERS as DocumentNode),
          variables: {
            data: {
              addresses,
              collections,
            },
          },
        }),
      });

      const json = await response.json();
      return json.data.getEvmNFTsByOwners as T[];
    },
    enabled: isIndexer && enabled,
    refetchOnWindowFocus: false,
  });

  const {
    data: dataOnchain,
    isLoading: loadingOnchain,
    isError: errorOnchain,
    refetch: refetchOnchain,
  } = useQuery<T | T[]>({
    queryKey: [
      'evmNftsOnchain',
      address,
      addresses,
      collections,
      chain,
      tokenId,
      ...additionalQueryKeys,
    ],
    queryFn: async () => {
      if (addresses !== undefined) {
        return await getEvmNfts<T>({
          addresses,
          collections,
          chain,
          dataSource: 'onchain',
        });
      }
      return await getEvmNfts<T>({
        collections,
        chain,
        dataSource: 'onchain',
        tokenId,
        address,
      });
    },
    enabled: isOnchain && enabled,
    refetchOnWindowFocus: false,
  });

  const data = isIndexer ? dataIndexer : dataOnchain;
  const loading = isIndexer ? loadingIndexer : loadingOnchain;
  const error = isIndexer ? errorIndexer : errorOnchain;
  const refetch = isIndexer ? refetchIndexer : refetchOnchain;

  return {
    data,
    loading,
    error,
    refetch,
  } as UseEvmNftsSingularResult<T> | UseEvmNftsPluralResult<T>;
}
